import type { ReactNode } from "react"
import Link from "next/link"

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Loader,
} from "@coval/ui"
import { ChevronRight, Trash2 } from "lucide-react"

import { useQueryParams } from "@/hooks/useQueryParams"

import {
  useTemplateDependencyAvailability,
  type TemplateDependencyResourceKind,
} from "../transport/hooks/useTemplateDependencyAvailability"

interface TemplateDependencyDeleteDialogProps {
  confirmTestId?: string
  isDeleting?: boolean
  note?: ReactNode
  onConfirm: () => void
  onOpenChange: (open: boolean) => void
  open: boolean
  prompt: ReactNode
  resourceId: string | null | undefined
  resourceKind: TemplateDependencyResourceKind
  title: string
}

const RESOURCE_LABEL_BY_KIND: Record<TemplateDependencyResourceKind, string> = {
  agent: "agent",
  metric: "metric",
  mutation: "mutation",
  persona: "persona",
  testSet: "test set",
}

export function TemplateDependencyDeleteDialog({
  open,
  ...props
}: TemplateDependencyDeleteDialogProps) {
  if (!open) {
    return null
  }

  return <OpenTemplateDependencyDeleteDialog {...props} open />
}

function OpenTemplateDependencyDeleteDialog({
  confirmTestId,
  isDeleting = false,
  note,
  onConfirm,
  onOpenChange,
  open,
  prompt,
  resourceId,
  resourceKind,
  title,
}: TemplateDependencyDeleteDialogProps) {
  const queryParams = useQueryParams()
  const availability = useTemplateDependencyAvailability(
    resourceId,
    resourceKind,
  )
  const isBlocked = availability.status === "blocked"
  const isCloseOnly = isBlocked || availability.status === "error"
  const canDelete = availability.status === "available"
  const templateCount = isBlocked ? availability.templates.length : 0
  const resourceLabel = RESOURCE_LABEL_BY_KIND[resourceKind]

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isDeleting) {
          onOpenChange(next)
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {(availability.status === "idle" ||
            availability.status === "checking") && (
            <div className="flex min-h-20 items-center justify-center">
              <Loader size="md" aria-label="Checking Templates" />
            </div>
          )}
          {availability.status === "error" && (
            <p className="text-coval-text-muted">
              We couldn&apos;t check whether Templates use this {resourceLabel}.
              Close this dialog and try again.
            </p>
          )}
          {isBlocked && (
            <div className="space-y-4">
              <p className="text-coval-text">
                {templateCount === 1
                  ? `Can’t delete this ${resourceLabel} because 1 Template relies on it.`
                  : `Can’t delete this ${resourceLabel} because ${templateCount} Templates rely on it.`}
              </p>
              <section aria-labelledby="blocking-templates-heading">
                <h3
                  id="blocking-templates-heading"
                  className="mb-2 text-sm font-semibold text-coval-text"
                >
                  Templates
                </h3>
                <ul className="max-h-72 divide-y divide-coval-border overflow-y-auto rounded-md border border-coval-border">
                  {availability.templates.map((template) => (
                    <li key={template.id}>
                      <Link
                        href={queryParams.getHref(
                          `/templates/${encodeURIComponent(template.id)}`,
                        )}
                        className="flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-surface-1 focus-visible:bg-surface-1 focus-visible:outline-none"
                      >
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-coval-text">
                          {template.displayName}
                        </span>
                        <ChevronRight
                          aria-hidden="true"
                          className="h-4 w-4 shrink-0 text-coval-text-muted"
                        />
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          )}
          {canDelete && (
            <>
              <p className="font-medium text-coval-text">{prompt}</p>
              {note ? (
                <p className="mt-3 mb-2 text-xs text-coval-text-muted">
                  {note}
                </p>
              ) : null}
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
            aria-label={isCloseOnly ? "Close" : "Cancel delete"}
          >
            {isCloseOnly ? "Close" : "Cancel"}
          </Button>
          {canDelete && (
            <Button
              variant="default"
              onClick={onConfirm}
              loading={isDeleting}
              data-testid={confirmTestId}
              aria-label="Confirm delete"
            >
              {!isDeleting && <Trash2 className="h-4 w-4" />}
              Delete
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
